import { MKUtils } from "./MKUtils";
import { MKPlatform } from ".";
import { MKAzukiRequest } from "../request";
import { MKProgramRestrictionError } from "../error";
import { MKHeaders, MKRequestQueryParams } from "../../alias";
import { MKAzukiRequestEndpoint, MKConstants } from "../../constants";
import { MKDrmType, MKProgramRestrictionType, MKStreamType } from "../../enums";
import { MKCdn, MKDrmConfig, MKProgramInfo, MKProgramRestrictions, MKRollInfo, MKTimeRange } from "../../interfaces";


/**
 * Utils for getting data from Azuki response object.
 * @internal
 */
export abstract class MKAzukiResponseUtils {
    /**
     * Get roll information from roll response.
     * @param response the roll response object.
     * @param requestBaseUrl the azuki request server baseUrl.
     * @param queryParams the URL query parameters object.
     * @param requestHeaders the azuki request headers object.
     * @returns an object containing roll information.
     */
    public static getRollInfo(response: any, requestBaseUrl: string, queryParams: MKRequestQueryParams, requestHeaders: MKHeaders): MKRollInfo {
        const sourceUrls = MKAzukiResponseUtils.getSourceUrls(response, queryParams);
        const drmType = MKAzukiResponseUtils.getDrmType(response);
        const rollInfo: MKRollInfo = {
            mediaId: "mediaId" in queryParams && queryParams["mediaId"] ? queryParams["mediaId"].toString() : "",
            isLive: response.live ?? false,
            isLiveEvent: response.live_event ?? false,
            message_id: response.message_id,
            beaconInterval: response.beacon_interval ?? 0,
            drmType: drmType,
            drm: MKAzukiResponseUtils.getDrmConfig(drmType, requestBaseUrl, queryParams, requestHeaders),
            streamType: MKAzukiResponseUtils.getStreamType(response),
            programInfo: MKAzukiResponseUtils.getProgramInfo(response),
            sourceUrl: sourceUrls.sourceUrl,
            sourceCdnProvider: sourceUrls.sourceCdnProvider,
            failoverSourceUrl: sourceUrls.failoverSourceUrl,
            failoverCdnProvider: sourceUrls.failoverCdnProvider,
            asidInitToken: response.asid_init_token,
            analytics_publish_percent: response.analytics_publish_percent,
            isLowLatencyPlayback: response.isLowLatencyPlayback,
            player_config: response.player_config
        };
        return rollInfo;
    }

    /**
     * Get stream type from the response object.
     *
     * @param response the response object to look for the stream type.
     * @returns the matching MKStreamType if available, else MKStreamType.Unknown is returned.
     */
    public static getStreamType(response: any): MKStreamType {
        if (MKUtils.isValidObject(response) && MKUtils.isValidString(response.package_type)) {
            if (response.package_type === MKStreamType.Hls) return MKStreamType.Hls;
            if (response.package_type === MKStreamType.Dash) return MKStreamType.Dash;
        }
        return MKStreamType.Unknown;
    }

    /**
     * Get DRM type from the response object.
     *
     * @param response the response object to look for the drm type.
     * @returns the matching MKDrmType if available, else MKDrmType.Unknown is returned.
     */
    public static getDrmType(response: any): MKDrmType {
        if (MKUtils.isValidObject(response) && MKUtils.isValidString(response.drm_type)) {
            if (response.drm_type === MKDrmType.Fairplay) return MKDrmType.Fairplay;
            if (response.drm_type === MKDrmType.Widevine) return MKDrmType.Widevine;
            if (response.drm_type === MKDrmType.Playready) return MKDrmType.Playready;
        }
        return MKDrmType.Unknown;
    }

    /**
     * Get available program restrictions from the response object.
     *
     * @param response the response object containing program restrictions/entitlements.
     * @returns the program restrictions object containing available restrictions.
     */
    public static getProgramRestrictions(response: any): MKProgramRestrictions {
        const programRestrictions: MKProgramRestrictions = {
            controlBlocking: [],
            playbackBlocking: []
        }

        if (MKUtils.isValidObject(response) && MKUtils.isValidObject(response.program_entitlements)) {
            Object.entries(response.program_entitlements).forEach(([key, value]) => {
                if (value == true) {
                    switch (key) {
                        case 'ts':
                            programRestrictions.controlBlocking.push(new MKProgramRestrictionError(MKProgramRestrictionType.Timeshift));
                            break;
                        case 'rn':
                            programRestrictions.controlBlocking.push(new MKProgramRestrictionError(MKProgramRestrictionType.Restart));
                            break;
                        case 'sr':
                            programRestrictions.controlBlocking.push(new MKProgramRestrictionError(MKProgramRestrictionType.SeekBackward));
                            break;
                        case 'sf':
                            programRestrictions.controlBlocking.push(new MKProgramRestrictionError(MKProgramRestrictionType.SeekForward));
                            break;
                        case 'ss':
                            programRestrictions.controlBlocking.push(new MKProgramRestrictionError(MKProgramRestrictionType.SeekSkip));
                            break;
                        case 'ct':
                            programRestrictions.controlBlocking.push(new MKProgramRestrictionError(MKProgramRestrictionType.CatchUp));
                            break;
                        case 'rb':
                            programRestrictions.controlBlocking.push(new MKProgramRestrictionError(MKProgramRestrictionType.Resume));
                            break;
                        //playback restrictions
                        case 'an':
                            if (MKPlatform.isAndroid()) {
                                programRestrictions.playbackBlocking.push(new MKProgramRestrictionError(MKProgramRestrictionType.AndroidBlocked));
                            }
                            break;
                        case 'io':
                            if (MKPlatform.isIOSDevice()) {
                                programRestrictions.playbackBlocking.push(new MKProgramRestrictionError(MKProgramRestrictionType.IOSBlocked));
                            }
                            break;
                        case 'ph':
                            if (MKPlatform.isPhone()) {
                                programRestrictions.playbackBlocking.push(new MKProgramRestrictionError(MKProgramRestrictionType.PhoneBlocked));
                            }
                            break;
                        case 'tb':
                            if (MKPlatform.isTablet()) {
                                programRestrictions.playbackBlocking.push(new MKProgramRestrictionError(MKProgramRestrictionType.TabletBlocked));
                            }
                            break;
                        case 'dt':
                            if (MKPlatform.isDesktop()) {
                                programRestrictions.playbackBlocking.push(new MKProgramRestrictionError(MKProgramRestrictionType.DesktopBlocked));
                            }
                            break;
                        case 'oh':
                            if (!MKAzukiRequest.inhomeStatus) {
                                programRestrictions.playbackBlocking.push(new MKProgramRestrictionError(MKProgramRestrictionType.OutofhomeBlocked));
                            }
                            break;
                        case 'ap':
                            programRestrictions.controlBlocking.push(new MKProgramRestrictionError(MKProgramRestrictionType.AirplayBlocked));
                            break;
                        //commenting the below two cases as it is currently not supported on this platform
                        // case 'ce':
                        //     programRestrictions.playbackBlocking.push(new MKProgramRestrictionError(MKProgramRestrictionType.CellularBlocked));
                        //     break;
                        // case 'wf':
                        //     programRestrictions.playbackBlocking.push(new MKProgramRestrictionError(MKProgramRestrictionType.WifiBlocked));
                        //     break;
                        default:
                            break;
                    }
                } else if (value == false) {
                    switch (key) {
                        case 'fw':
                            programRestrictions.controlBlocking.push(new MKProgramRestrictionError(MKProgramRestrictionType.FastForward));
                            break;
                        case 'rw':
                            programRestrictions.controlBlocking.push(new MKProgramRestrictionError(MKProgramRestrictionType.Rewind));
                            break;
                        default:
                            break;
                    }
                }
            });
        }
        return programRestrictions;
    }

    /**
     * Get the program info from response object.
     *
     * @param response the response object to look for corresponding program info.
     * @returns the program info object.
     */
    public static getProgramInfo(response: any): MKProgramInfo {
        const programInfo: MKProgramInfo = {
            programRestrictions: MKAzukiResponseUtils.getProgramRestrictions(response)
        }

        if (MKUtils.isValidObject(response) && MKUtils.isValidString(response.start_time) && MKUtils.isValidString(response.end_time)) {
            const timeRange: MKTimeRange = {
                start: MKUtils.dateISOStringToTimestamp(response.start_time) / 1000,
                end: MKUtils.dateISOStringToTimestamp(response.end_time) / 1000
            }
            programInfo.timeRange = timeRange;
        }

        return programInfo;
    }

    /**
     * Get the DRM configuration for the specified DRMType.
     * @param drmType the requested DRMType.
     * @returns the DRM configuration for the requested DRMType.
     */
    private static getDrmConfig(drmType: MKDrmType, requestBaseUrl: string, queryParams: MKRequestQueryParams, requestHeaders: MKHeaders): MKDrmConfig {
        // construct the drm object for the given drm type and only append LA_URL, Certificate URL and request headers
        // and all necessary callbacks for handling Fairplay DRM will be handled in MKBitmovinPlayer.ts
        const drm: MKDrmConfig = {};
        drm.immediateLicenseRequest = MKPlatform.isTizenTV() || MKPlatform.isOperaTV() || MKPlatform.isComcastX1() ? true : undefined;
        if (drmType === MKDrmType.Widevine) {
            drm.widevine = {
                headers: requestHeaders,
                LA_URL: MKUtils.updateURLQueryParams(MKAzukiRequestEndpoint.WV_LICENSE.get(requestBaseUrl), queryParams)
            }
        } else if (drmType === MKDrmType.Playready) {
            drm.playready = {
                headers: requestHeaders,
                LA_URL: MKUtils.updateURLQueryParams(MKAzukiRequestEndpoint.PR_LICENSE.get(requestBaseUrl), queryParams)
            }
            if (MKPlatform.isPlayStation5() || MKPlatform.isPlayStation4() || MKPlatform.isWebOS()) {
                // additional platfrom specific settings for Playready DRM
                Object.assign(drm.playready, {
                    utf8message: true,
                    plaintextChallenge: true
                });
            }
        } else if (drmType === MKDrmType.Fairplay) {
            drm.fairplay = {
                headers: requestHeaders,
                LA_URL: MKUtils.updateURLQueryParams(MKAzukiRequestEndpoint.FP_LICENSE.get(requestBaseUrl), queryParams),
                certificateURL: MKUtils.updateURLQueryParams(MKAzukiRequestEndpoint.FP_CERTIFICATE.get(requestBaseUrl), queryParams)
            }
        }
        return drm;
    }

    /**
     * Get the source URL's from the response object.
     *
     * @param response the response object containing preferred and failover source URL's
     * @param sessionId the unique playback session identifier
     * @returns object containing source URL's
     */
    private static getSourceUrls(response: any, queryParams: MKRequestQueryParams): {sourceUrl: string, sourceCdnProvider:string, failoverSourceUrl?: string[], failoverCdnProvider?: string[]} {
        let manifestPath = response.manifest_uri;

        // append sessionId for manifest URL
        const sessionId = queryParams["sessionId"]?.toString();
        if (!!sessionId) {
            manifestPath = MKUtils.updateURLQueryParams(manifestPath, {sessionId: sessionId});
        }

        let cdns: MKCdn[] = response.cdns.cdn.map((cdn: any): MKCdn => {
            return {
                name: cdn.name,
                retrievalType: cdn.retrieval_type,
                baseUrl: cdn.base_uri,
                priority: cdn.priority,
                weight: cdn.threshold,
                routingPercent: 0
            }
        });

        let sourceUrls: {sourceUrl: string, sourceCdnProvider: string, failoverSourceUrl?: string[], failoverCdnProvider?: string[]} = {
            sourceUrl: "",
            sourceCdnProvider: ""
        }

        // append personal info if available
        if (!!response.personal_info) {
            const personalInfo = MKUtils.jsonParse(MKUtils.base64Decode(response.personal_info));
            manifestPath = MKUtils.updateURLQueryParams(manifestPath, personalInfo ?? undefined);
        }

        // append device type if available
        if (!!response.platform) {
            manifestPath = MKUtils.updateURLQueryParams(manifestPath, {"devicetype": response.platform});
        }
        
        // Get shuffled CDN list
        const shuffledCdns = MKAzukiResponseUtils.shuffleCdns(cdns);

        // Get preferred CDN from shuffled CDN list
        const preferredCdn = shuffledCdns?.shift();
        if (preferredCdn) {
            sourceUrls.sourceUrl = `${preferredCdn.baseUrl}/${manifestPath}`;
            sourceUrls.sourceCdnProvider = `${preferredCdn.name}`;
        }

        // Get failover CDNs from shuffled CDN list
        const failoverCdn: MKCdn[] | undefined = shuffledCdns;
        if (Array.isArray(failoverCdn) && failoverCdn.length > 0) {
            sourceUrls.failoverSourceUrl = failoverCdn.map(cdn => `${cdn.baseUrl}/${manifestPath}`);
            sourceUrls.failoverCdnProvider = failoverCdn.map(cdn => `${cdn.name}`);
        }

        // Return the manifest URL's
        return sourceUrls;
    }

    /**
     * From the list of CDN's return shuffled cdn list if available considering both Priority and Threshold.
     * Per priority 2 CDNs are returned if exists
     *
     * Note: This method changes the length of the array.
     *
     * @param cdns the list of available cdns.
     * @returns the shuffled cdn list if cdns available, undefined otherwise.
     */
    private static shuffleCdns(cdns: MKCdn[]): MKCdn[] | undefined {
        const finalCdnList: MKCdn[] = [];

        if (cdns.length === 0) return finalCdnList;
        if (cdns.length === 1) return  cdns;

        const groupedCdns = Object.values(
            cdns.reduce((acc,cdn) => {
                acc[cdn.priority] = acc[cdn.priority] || [];
                acc[cdn.priority].push(cdn);
                return acc;
            }, {} as Record<number, MKCdn[]>)
        );

        for (const group of groupedCdns) {
            let selectedCdns: MKCdn[] = [];

            if (group.length > 1) {
                // when there are more than 1 cdn's in the grouped by priority list
                const totalCdnWeight = group.reduce((sum, cdn) => sum + cdn.weight, 0);
                if (totalCdnWeight > 0) {
                    let position = 0;
                    const randNum = parseFloat(Math.random().toFixed(2));
                    // update routingPercent for the cdn items
                    group.forEach(cdn => {
                        cdn.routingPercent = parseFloat((cdn.weight / totalCdnWeight).toFixed(2));
                        const rangeStart = position;
                        const rangeEnd = rangeStart + cdn.routingPercent;
                        cdn.thresholdRange = `${rangeStart}:${rangeEnd}`;
                        position = rangeEnd;
                    });
                    
                    const preferredIndex = group.findIndex(cdn => cdn.thresholdRange && randNum <= parseFloat(cdn.thresholdRange.split(":")[1]));
                    const selectedCdn = preferredIndex >= 0 ? group[preferredIndex] : group[0];
                    selectedCdns.push(selectedCdn);

                    // sorting the group based on descending routingPercent
                    const sortedByRoutingPercent = [...group].sort((a, b) => b.routingPercent - a.routingPercent)
                    const fallbackCdns = sortedByRoutingPercent.filter(cdn => cdn.baseUrl !== selectedCdn.baseUrl);
                    if (fallbackCdns.length > 0) {
                        selectedCdns.push(...fallbackCdns.slice(0, MKConstants.MAX_CDNS_PER_PRIORITY_GROUP - 1));
                    }
                } else {
                    selectedCdns = group.slice(0, MKConstants.MAX_CDNS_PER_PRIORITY_GROUP);
                }
            } else {
                selectedCdns = group;
            }

            finalCdnList.push(...selectedCdns);
        }
        return finalCdnList;
    }
}